import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row, Col, Card, Collapse, Descriptions, Table, Tag, Typography,
  Button, Divider, Space, Statistic, Breadcrumb, Alert, Input,
} from "antd";
import {
  GlobalOutlined, TeamOutlined,
  DollarOutlined, LinkOutlined,
  FlagOutlined,
  BankOutlined,
  ThunderboltOutlined, PhoneOutlined,
  NumberOutlined, LineChartOutlined,
  SoundOutlined, PauseCircleOutlined,
} from "@ant-design/icons";
import { getAnthemUrl } from "../../utils/cifApi";
import { fetchCifProfile, fetchCifAllIndicators, fetchCifIndicatorTimeSeries, downloadCifExportReport } from "../../utils/cifApi";
import { fetchOicCountries, getFlagUrl } from "../../utils/countriesApi";
import TimeSeriesModal from "../../components/charts/TimeSeriesModal";
import ExportButton from "../../components/ExportButton";
import "./CifProfile.scss";

const { Title, Text, Paragraph } = Typography;

const AnthemPlayer = ({ cCode, anthemText }) => {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio(getAnthemUrl(cCode));
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [cCode]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const handleEnded = () => setPlaying(false);
    a.addEventListener("ended", handleEnded);
    return () => a.removeEventListener("ended", handleEnded);
  }, [playing]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      a.currentTime = 0;
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  return (
    <span>
      {anthemText}
      <Button
        type="text"
        size="small"
        icon={playing ? <PauseCircleOutlined /> : <SoundOutlined />}
        onClick={toggle}
        style={{ color: "#2F83C5", marginLeft: 8 }}
      >
        {playing ? t("cif.profile.stop") : t("cif.profile.play")}
      </Button>
    </span>
  );
};
const { Panel } = Collapse;

const sectionIcons = {
  background: <FlagOutlined />,
  geography: <GlobalOutlined />,
  demography: <TeamOutlined />,
  economy: <DollarOutlined />,
  figures: null,
  logistics: <ThunderboltOutlined />,
  links: <LinkOutlined />,
};



function formatIndicatorValue(value, unit) {
  if (value === null || value === undefined) return "—";
  if (typeof value !== "number") return String(value);
  if (Number.isInteger(value)) return value.toLocaleString("en-US");
  if (Math.abs(value) >= 1) return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return value.toFixed(4);
}

function formatCompact(value) {
  if (value === null || value === undefined) return "—";
  if (value >= 1e12) return (value / 1e12).toFixed(2) + "T";
  if (value >= 1e9) return (value / 1e9).toFixed(1) + "B";
  if (value >= 1e6) return (value / 1e6).toFixed(1) + "M";
  return Math.round(value).toLocaleString("en-US");
}

function buildLinkList(links, t) {
  if (!links) return [];
  const keys = [
    "national_statistical_office",
    "central_bank",
    "development_agency",
    "ministry_of_foreign_affairs",
    "ministry_of_agriculture",
    "ministry_of_education",
    "ministry_of_health",
    "ministry_of_culture",
    "ministry_of_economy",
    "ministry_of_planning",
    "ministry_of_internal_affairs",
    "ministry_of_energy",
    "ministry_of_environment",
    "ministry_of_finance",
    "ministry_of_information",
    "ministry_of_labor",
    "ministry_of_transportation",
    "ministry_of_women",
    "ministry_of_youth",
    "higer_education",
  ];
  return keys
    .map((key) => ({ name: t(`cif.links.${key}`), url: links[key] }))
    .filter((e) => e.url && e.url !== "N/A");
}

function parseAreaPct(totalStr, partStr) {
  const total = parseFloat(String(totalStr || "").replace(/[^0-9.]/g, ""));
  const part = parseFloat(String(partStr || "").replace(/[^0-9.]/g, ""));
  if (!total || !part) return null;
  return Math.round((part / total) * 1000) / 10;
}

export default function CifProfile() {
  const { t, i18n } = useTranslation();
  const { c_code } = useParams();
  const navigate = useNavigate();
  const cCodeNum = parseInt(c_code, 10);
  const [country, setCountry] = useState(null);

  const [profile, setProfile] = useState(null);
  const [allIndicators, setAllIndicators] = useState(null);
  const [activeKeys, setActiveKeys] = useState(["1"]);
  const [searchText, setSearchText] = useState("");
  const [openCats, setOpenCats] = useState([]);
  const debounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tsModal, setTsModal] = useState({ visible: false, countryName: "", indicatorName: "", unit: "", data: null });

  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchText), 250);
    return () => clearTimeout(debounceRef.current);
  }, [searchText]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchOicCountries().then((countries) => {
      if (!cancelled) {
        const match = countries.find((c) => c.c_code === cCodeNum);
        setCountry(match || null);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [cCodeNum, i18n.language]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchCifProfile(cCodeNum)
      .then((data) => {
        if (!cancelled) {
          setProfile(data);
          setLoading(false);
          fetchCifAllIndicators(cCodeNum)
            .then((allData) => { if (!cancelled) setAllIndicators(allData); })
            .catch(() => {});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [cCodeNum, i18n.language]);

  const handleExportReport = useCallback(async () => {
    if (!country) return;
    try {
      const blob = await downloadCifExportReport(cCodeNum);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `CIF_${country.name.replace(/\s+/g, "_")}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
    }
  }, [country, cCodeNum]);

  const openTimeSeries = useCallback(async (indicator) => {
    try {
      const data = await fetchCifIndicatorTimeSeries(cCodeNum, indicator.indicator_id);
      setTsModal({
        visible: true,
        countryName: country?.name || t("cif.profile.countryFallback", { code: cCodeNum }),
        indicatorName: indicator.indicator_name,
        unit: indicator.unit || "",
        data: data.data || [],
      });
    } catch {
    }
  }, [cCodeNum, country, t]);

  const activeCategories = debouncedSearch.trim()
    ? allIndicators?.categories
        .filter((cat) => {
          const q = debouncedSearch.toLowerCase().trim();
          return cat.cat_name.toLowerCase().includes(q) ||
            cat.indicators.some((ind) => ind.indicator_name.toLowerCase().includes(q));
        })
        .map((cat) => String(cat.cat_code))
    : openCats;

  const bg = profile?.background;
  const geo = profile?.geography;
  const demo = profile?.demography;
  const econ = profile?.economy;
  const pol = profile?.politics;
  const logi = profile?.logistics;
  const links = profile?.links;
  const inds = profile?.key_indicators || {};

  const linkEntries = buildLinkList(links, t);
  const landPct = geo ? parseAreaPct(geo.total_area, geo.total_land_area) : null;

  const sectionHeader = (icon, title, color = "#2F83C5") => (
    <Space>
      <div style={{
        width: 28, height: 28, borderRadius: 6,
        background: `${color}18`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 15, color,
      }}>{icon}</div>
      <Text strong style={{ fontSize: 15, color }}>{title}</Text>
    </Space>
  );

  const Di = (label, value, span) => {
    if (!value) return null;
    return <Descriptions.Item label={label} span={span || 1}>{value}</Descriptions.Item>;
  };

  const KNOWN_BG = new Set(["cid","official_name","flag","anthem","anthem_in_official_language","independence_date","national_day","capital","other_cities","official_languages","historical_background"]);
  const KNOWN_GEO = new Set(["cid","location","total_area","total_land_area","total_water_area","land_boundaries","border_countries","coastline","climate_and_weather"]);
  const KNOWN_DEMO = new Set(["cid","nationality","ethnic_groups","ethnic_groups_percent","religions","religions_note","human_development_index","human_development_index_rank","hdi_year","sociological_overview"]);
  const KNOWN_ECO = new Set(["cid","currency_unit","economic_complexity_score","economic_complexity_score_rank","ecs_year","export_partners","import_partners","oic_export_partners","oic_import_partners","natural_resources","main_agricultural_products","economy_overview","economy_relationship_with_turkey"]);
  const KNOWN_POL = new Set(["cid","government_type","chief_of_state","title_president","assumed_office_president","link_of_presidency","head_of_government","title_pminister","assumed_office_pminister","link_of_prime_ministry","cabinet","legislative_branch","name_of_parliament","head_of_parliament","secondary_parliament","head_of_secondary_parliament","structure_of_parliament","members","members_second","number_of_women","term","last_renewal_date","website_second","last_renewal_date_second","voting_system","voting_requirements","website_of_parliament","judicial_branch","highest_court","subordinate_court","judge_selection","parties_and_leaders","political_overview","political_relationship_with_turkey"]);
  const KNOWN_PART = new Set(["cid","participation_in_international_organizations","international_agencies_presided","candidacies_of_member_countries","member_oic_since","oic_institutions_hosted","sesric_events_hosted","mou","sesric_workshop_participated","trainings_as_provider","trainings_as_beneficiary","cooperation_opportunities_with_sesric_and_oic","appointment_possibilities"]);
  const KNOWN_LOG = new Set(["cid","time_difference","country_code","exchange_rate","info_of_embassy","name_of_ambassador","email_of_embassy","telephone_of_embassy","fax_of_embassy","address_of_embassy","name_of_ambassador2","email_of_embassy2","telephone_of_embassy2","fax_of_embassy2","address_of_embassy2","visa_issues","electricity","health_related_issues","weather","prayer_times","places_to_visit","police_number","ambulance_number","fire_number"]);

  const humanLabel = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const extraItems = (data, known) => {
    if (!data) return null;
    return Object.entries(data)
      .filter(([k, v]) => !known.has(k) && k !== "cid" && v)
      .map(([k, v]) => (
        <Descriptions.Item key={`_${k}`} label={humanLabel(k)} span={2}>{v}</Descriptions.Item>
      ));
  };

  if (loading) {
    return (
      <div className="cif-profile__loading">
        <div className="cif-profile__skel-breadcrumb" />
        <div className="cif-profile__skel-hero" />
        <div className="cif-profile__skel-stats">
          {[1, 2, 3, 4].map((i) => (
            <div
              className="cif-profile__skel-stat-card"
              key={i}
              style={{ animationDelay: `${i * 0.06}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div style={{ padding: 40 }}>
        <Breadcrumb
          items={[
            { title: <a onClick={() => navigate("/cif")}>{t("cif.profile.home")}</a> },
            { title: country ? country.name : t("cif.profile.countryFallback", { code: c_code }) },
          ]}
          style={{ marginBottom: 12 }}
        />
        <Card style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <Alert
            message={t("cif.profile.notAvailableTitle")}
            description={t("cif.profile.notAvailableDesc")}
            type="warning"
            showIcon
            action={
              <Button
                size="small"
                onClick={() => navigate("/cif")}
                style={{ borderRadius: 6 }}
              >
                {t("cif.profile.backToMap")}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 4 }}>
      <div className="cif-profile__animate-in">
        <Breadcrumb
          items={[
            { title: <a onClick={() => navigate("/cif")}>{t("cif.profile.home")}</a> },
            { title: country ? country.name : t("cif.profile.countryFallback", { code: c_code }) },
          ]}
          style={{ marginBottom: 12 }}
        />
      </div>

      <Card
        style={{
          marginBottom: 16,
          background: "#2F83C5",
          color: "#fff",
          borderRadius: 12,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 6px 24px rgba(47, 131, 197,0.25)",
          animationDelay: '0.1s',
        }}
        className="cif-profile__animate-in"
        styles={{
          body: { padding: "28px 32px", position: "relative", zIndex: 1 },
        }}
      >
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", top: -40, right: -40, width: 200, height: 200,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.04)",
          pointerEvents: "none",
        }} />
        {/* Flag moved to the Background accordion section */}
        <Row gutter={24} align="middle" style={{ position: "relative", zIndex: 2, minHeight: 100 }}>
          <Col flex="auto">
            <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
              {country?.name || t("cif.profile.countryFallback", { code: c_code })}
            </Title>
            <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>
              {bg?.official_name || ""}{bg && geo ? " — " : ""}{geo?.location || ""}
            </Text>
          </Col>

        </Row>
      </Card>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} className="cif-profile__stats">
        {inds.population_total && (() => {
          const i = inds.population_total;
          return (
            <Col xs={24} sm={12} key="pop">
              <Card
                size="small" hoverable
                style={{
                  borderRadius: 10,
                  background: "#2F83C5",
                  border: "none",
                  boxShadow: "0 4px 14px rgba(47, 131, 197,0.25)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  animationDelay: '0.2s',
                }}
                className="cif-profile__animate-in"
                styles={{ body: { padding: "12px 16px" } }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(47, 131, 197,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 14px rgba(47, 131, 197,0.25)"; }}
              >
                <Statistic
                  title={<span style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>{t("cif.profile.population")} ({i.year})</span>}
                  value={i.value}
                  precision={0}
                  prefix={<TeamOutlined style={{ color: "rgba(255,255,255,0.6)" }} />}
                  valueStyle={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
                />
              </Card>
            </Col>
          );
        })()}
        {inds.gdp_current_prices && (() => {
          const i = inds.gdp_current_prices;
          return (
            <Col xs={24} sm={12} key="gdp">
              <Card
                size="small" hoverable
                style={{
                  borderRadius: 10,
                  background: "#2F83C5",
                  border: "none",
                  boxShadow: "0 4px 14px rgba(47, 131, 197,0.25)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  animationDelay: '0.28s',
                }}
                className="cif-profile__animate-in"
                styles={{ body: { padding: "12px 16px" } }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(47, 131, 197,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 14px rgba(47, 131, 197,0.25)"; }}
              >
                <Statistic
                  title={<span style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>{t("cif.profile.gdpShort")} ({i.year})</span>}
                  value={formatCompact(i.value)}
                  prefix={<BankOutlined style={{ color: "rgba(255,255,255,0.6)" }} />}
                  suffix={<span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>{i.unit || ""}</span>}
                  valueStyle={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
                />
              </Card>
            </Col>
          );
        })()}
        {inds.gdp_per_capita && (() => {
          const i = inds.gdp_per_capita;
          return (
            <Col xs={24} sm={12} key="gdp-pc">
              <Card
                size="small" hoverable
                style={{
                  borderRadius: 10,
                  background: "#2F83C5",
                  border: "none",
                  boxShadow: "0 4px 14px rgba(47, 131, 197,0.25)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  animationDelay: '0.36s',
                }}
                className="cif-profile__animate-in"
                styles={{ body: { padding: "12px 16px" } }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(47, 131, 197,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 14px rgba(47, 131, 197,0.25)"; }}
              >
                <Statistic
                  title={<span style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>{t("cif.profile.gdpPerCapitaShort")} ({i.year})</span>}
                  value={i.value}
                  precision={0}
                  prefix={<LineChartOutlined style={{ color: "rgba(255,255,255,0.6)" }} />}
                  suffix={<span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>{i.unit || ""}</span>}
                  valueStyle={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
                />
              </Card>
            </Col>
          );
        })()}
        {geo?.total_area && (
          <Col xs={24} sm={12}>
            <Card
              size="small" hoverable
              style={{
                borderRadius: 10,
                background: "#2F83C5",
                border: "none",
                boxShadow: "0 4px 14px rgba(47, 131, 197,0.25)",
                transition: "transform 0.2s, box-shadow 0.2s",
                animationDelay: '0.44s',
              }}
              className="cif-profile__animate-in"
              styles={{ body: { padding: "12px 16px" } }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(47, 131, 197,0.35)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 14px rgba(47, 131, 197,0.25)"; }}
            >
              <Statistic
                title={<span style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>{t("cif.profile.totalArea")}</span>}
                value={geo.total_area}
                prefix={<GlobalOutlined style={{ color: "rgba(255,255,255,0.6)" }} />}
                valueStyle={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
              />
            </Card>
          </Col>
        )}
      </Row>

      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => {
          setActiveKeys(keys);
        }}
        expandIconPosition="right"
        className="cif-profile__animate-in"
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          border: "1px solid #e8e8e8",
          animationDelay: '0.6s',
        }}
      >
        {bg && (
        <Panel
          header={sectionHeader(sectionIcons.background, `1. ${t("cif.sections.1.title").toUpperCase()}`)}
          key="1"
          style={{ borderRadius: 8, marginBottom: 2 }}
        >
          <Row gutter={24} align="stretch">
            <Col xs={24} md={16}>
              <Descriptions column={1} size="small" bordered
                style={{ borderRadius: 8, overflow: "hidden" }}
                styles={{
                  label: { background: "#d4e6f5", fontWeight: 600, fontSize: 13, color: "#000" },
                  content: { fontSize: 14, color: "#000" },
                }}
              >
                {Di(t("cif.profile.officialName"), bg.official_name, 2)}
                {(bg.anthem || bg.national_day || bg.anthem_in_official_language) && (
                  <Descriptions.Item label={t("cif.profile.nationalAnthem")} span={2}>
                    <div style={{ lineHeight: 1.6 }}>
                      <div style={{ marginBottom: 4 }}>{bg.anthem && <AnthemPlayer cCode={c_code} anthemText={bg.anthem} />}</div>
                      <div style={{ fontSize: 13.5, fontStyle: "italic", paddingLeft: 10, borderLeft: "2px solid #b5d1ef" }}>
                        {bg.national_day && <div><strong>{t("cif.profile.nationalDay")}</strong> {bg.national_day}</div>}
                        {bg.anthem_in_official_language && <div><strong>{t("cif.profile.inOriginalLanguage")}</strong> {bg.anthem_in_official_language}</div>}
                      </div>
                    </div>
                  </Descriptions.Item>
                )}
                {Di(t("cif.profile.independenceDate"), bg.independence_date)}
                {(bg.capital || bg.other_cities) && (
                  <Descriptions.Item label={t("cif.profile.capitalCity")} span={2}>
                    <div style={{ lineHeight: 1.6 }}>
                      <span>{bg.capital || ""}</span>
                      <div style={{ fontSize: 13.5, fontStyle: "italic", paddingLeft: 10, marginTop: 2, borderLeft: "2px solid #b5d1ef" }}>
                        <div><strong>{t("cif.profile.otherMajorCities")}</strong> {bg.other_cities || ""}</div>
                      </div>
                    </div>
                  </Descriptions.Item>
                )}
                {Di(t("cif.profile.officialLanguages"), bg.official_languages, 2)}
                {extraItems(bg, KNOWN_BG)}
              </Descriptions>
            </Col>
            <Col xs={24} md={8} style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              {country?.iso_a2 && (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 8,
                    border: "1px solid #e8e8e8",
                    marginBottom: bg.historical_background ? 12 : 0,
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 0,
                    overflow: "hidden",
                    padding: 16,
                  }}
                >
                  <img
                    src={getFlagUrl(country.c_code)}
                    alt={country.name}
                    style={{ display: "block", width: "100%", height: "auto", maxWidth: 320 }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              )}
              {bg.historical_background && (
              <Card title={t("cif.profile.historicalBackground")} size="small"
                style={{
                  background: "#d4e6f5",
                  borderRadius: 8,
                  border: "1px solid #e8e8e8",
                }}
              >
                <Paragraph style={{ fontSize: 13, margin: 0, lineHeight: 1.6, color: "#444" }}>
                  {bg.historical_background}
                </Paragraph>
              </Card>
              )}
            </Col>
          </Row>
        </Panel>
        )}

        {geo && (
        <Panel
          header={sectionHeader(sectionIcons.geography, `2. ${t("cif.sections.2.title").toUpperCase()}`)}
          key="2"
          style={{ borderRadius: 8, marginBottom: 2 }}
        >
          <Row gutter={24}>
            <Col xs={24} md={16}>
              <Descriptions column={1} size="small" bordered
                style={{ borderRadius: 8, overflow: "hidden" }}
                styles={{
                  label: { background: "#d4e6f5", fontWeight: 600, fontSize: 13, color: "#000" },
                  content: { fontSize: 14, color: "#000" },
                }}
              >
                {Di(t("cif.profile.location"), geo.location, 2)}
                {(geo.total_area || geo.total_land_area || geo.total_water_area) && (
                  <Descriptions.Item label={t("cif.profile.totalArea")} span={2}>
                    <div style={{ lineHeight: 1.6 }}>
                      <span>{geo.total_area || ""}</span>
                      <div style={{ fontSize: 13.5, fontStyle: "italic", paddingLeft: 10, marginTop: 2, borderLeft: "2px solid #b5d1ef" }}>
                        <div><strong>{t("cif.profile.landArea")}</strong> {geo.total_land_area || ""}</div>
                        <div><strong>{t("cif.profile.waterArea")}</strong> {geo.total_water_area || ""}</div>
                      </div>
                    </div>
                  </Descriptions.Item>
                )}
                {(geo.land_boundaries || geo.border_countries) && (
                  <Descriptions.Item label={t("cif.profile.landBoundaries")} span={2}>
                    <div style={{ lineHeight: 1.6 }}>
                      <span>{geo.land_boundaries || ""}</span>
                      <div style={{ fontSize: 13.5, fontStyle: "italic", paddingLeft: 10, marginTop: 2, borderLeft: "2px solid #b5d1ef" }}>
                        <div><strong>{t("cif.profile.borderCountries")}</strong> {geo.border_countries || ""}</div>
                      </div>
                    </div>
                  </Descriptions.Item>
                )}
                {Di(t("cif.profile.coastline"), geo.coastline, 2)}
                {(geo.climate_and_weather || landPct !== null) && (
                  <Descriptions.Item label={t("cif.profile.climateWeather")} span={2}>
                    <div style={{ lineHeight: 1.6 }}>
                      {geo.climate_and_weather && <div>{geo.climate_and_weather}</div>}
                      {landPct !== null && (
                        <div style={{ marginTop: geo.climate_and_weather ? 8 : 0, paddingLeft: 10, borderLeft: "2px solid #b5d1ef", fontStyle: "italic", fontSize: 13.5, lineHeight: 1.8 }}>
                          <div style={{ fontWeight: 600, fontStyle: "normal", marginBottom: 2 }}>{t("cif.profile.areaBreakdown")}</div>
                          <div><strong>{t("cif.profile.land")}</strong> {landPct}%</div>
                          <div><strong>{t("cif.profile.water")}</strong> {Math.round((100 - landPct) * 10) / 10}%</div>
                        </div>
                      )}
                    </div>
                  </Descriptions.Item>
                )}
                {extraItems(geo, KNOWN_GEO)}
              </Descriptions>
            </Col>
          </Row>
        </Panel>
        )}

        {demo && (
        <Panel
          header={sectionHeader(sectionIcons.demography, `3. ${t("cif.sections.3.title").toUpperCase()}`)}
          key="3"
          style={{ borderRadius: 8, marginBottom: 2 }}
        >
          <Row gutter={24}>
            <Col xs={24} md={16}>
              <Descriptions column={1} size="small" bordered
                style={{ borderRadius: 8, overflow: "hidden" }}
                styles={{
                  label: { background: "#d4e6f5", fontWeight: 600, fontSize: 13, color: "#000" },
                  content: { fontSize: 14, color: "#000" },
                }}
              >
                {Di(t("cif.profile.nationality"), demo.nationality, 2)}
                {Di(t("cif.profile.ethnicGroups"), demo.ethnic_groups, 2)}
                {Di(t("cif.profile.religions"), demo.religions, 2)}
                {(demo.human_development_index || demo.human_development_index_rank) && (
                  <Descriptions.Item label={t("cif.profile.hdi")} span={2}>
                    <div style={{ lineHeight: 1.6 }}>
                      {demo.human_development_index && (
                        <div><strong>{t("cif.profile.hdiValue")}</strong> {demo.human_development_index} {demo.hdi_year ? `(${demo.hdi_year})` : ""}</div>
                      )}
                      {demo.human_development_index_rank && (
                        <div><strong>{t("cif.profile.worldRank")}</strong> {demo.human_development_index_rank}/193</div>
                      )}
                    </div>
                  </Descriptions.Item>
                )}
                {inds.population_total && (() => {
                  const i = inds.population_total;
                  return (
                    <Descriptions.Item label={t("cif.profile.keyPopulationIndicators")} span={2}>
                      <div style={{ lineHeight: 1.6 }}>
                        <div><strong>{t("cif.profile.populationTotal", { year: i.year })}</strong> {i.value != null ? i.value.toLocaleString("en-US") : "—"} {i.unit || ""}</div>
                        <div style={{ paddingLeft: 10, borderLeft: "2px solid #519BD6", fontStyle: "italic", fontSize: 13.5, marginTop: 4, lineHeight: 1.8 }}>
                          {inds.population_growth_rate && (() => {
                            const g = inds.population_growth_rate;
                            return <div><strong>{t("cif.profile.annualGrowthRate", { year: g.year })}</strong> {g.value != null ? g.value.toFixed(2) : "—"} {g.unit || ""}</div>;
                          })()}
                          {inds.fertility_rate && (() => {
                            const f = inds.fertility_rate;
                            return <div><strong>{t("cif.profile.fertilityRate", { year: f.year })}</strong> {f.value != null ? f.value.toFixed(2) : "—"} {f.unit || ""}</div>;
                          })()}
                          {inds.population_density && (() => {
                            const d = inds.population_density;
                            return <div><strong>{t("cif.profile.populationDensity", { year: d.year })}</strong> {d.value != null ? d.value.toFixed(2) : "—"} {d.unit || ""}</div>;
                          })()}
                          {inds.adult_literacy_rate && (() => {
                            const a = inds.adult_literacy_rate;
                            return <div><strong>{t("cif.profile.adultLiteracyRate", { year: a.year })}</strong> {a.value != null ? a.value.toFixed(2) : "—"} {a.unit || ""}</div>;
                          })()}
                        </div>
                      </div>
                    </Descriptions.Item>
                  );
                })()}
                {extraItems(demo, KNOWN_DEMO)}
              </Descriptions>
            </Col>
            {pol && (
            <Col xs={24} md={8}>
              <Card size="small"
                style={{
                  background: "linear-gradient(135deg, #d4e6f5, #d4e6f5)",
                  borderRadius: 8,
                  border: "1px solid #e8e8e8",
                  marginBottom: 8,
                }}
              >
                <Text strong style={{ fontSize: 13, color: "#2F83C5", display: "block", marginBottom: 8 }}>{t("cif.profile.politicalStructure")}</Text>
                <Descriptions column={1} size="small">
                  {pol.government_type ? <Descriptions.Item label={<span style={{ fontSize: 12 }}>{t("cif.profile.governmentType")}</span>}>{pol.government_type}</Descriptions.Item> : null}
                  {pol.chief_of_state ? <Descriptions.Item label={<span style={{ fontSize: 12 }}>{t("cif.profile.chiefOfState")}</span>}>{pol.chief_of_state}</Descriptions.Item> : null}
                  {pol.name_of_parliament ? <Descriptions.Item label={<span style={{ fontSize: 12 }}>{t("cif.profile.parliament")}</span>}>{pol.name_of_parliament}</Descriptions.Item> : null}
                  {extraItems(pol, KNOWN_POL)}
                </Descriptions>
              </Card>
              {profile.cabinet_members && profile.cabinet_members.length > 0 && (
              <Card size="small" style={{ borderRadius: 8, border: "1px solid #e8e8e8" }}>
                <Text strong style={{ fontSize: 12, display: "block", marginBottom: 8 }}>{t("cif.profile.cabinetMembers")}</Text>
                <Table
                  dataSource={profile.cabinet_members}
                  columns={[
                    { title: <span style={{ fontSize: 12 }}>{t("cif.profile.title")}</span>, dataIndex: "title", key: "title", render: v => <Text strong style={{ fontSize: 12 }}>{v}</Text> },
                    { title: <span style={{ fontSize: 12 }}>{t("cif.profile.name")}</span>, dataIndex: "name", key: "name", render: n => <span style={{ fontSize: 12 }}>{n}</span> },
                  ]}
                  pagination={false}
                  size="small"
                  rowKey="title"
                />
              </Card>
              )}
            </Col>
            )}
          </Row>
        </Panel>
        )}

        {econ && (
        <Panel
          header={sectionHeader(sectionIcons.economy, `4. ${t("cif.sections.4.title").toUpperCase()}`)}
          key="4"
          style={{ borderRadius: 8, marginBottom: 2 }}
        >
          <Row gutter={24}>
            <Col xs={24} md={16}>
              <Descriptions column={1} size="small" bordered
                style={{ borderRadius: 8, overflow: "hidden" }}
                styles={{
                  label: { background: "#d4e6f5", fontWeight: 600, fontSize: 13, color: "#000" },
                  content: { fontSize: 14, color: "#000" },
                }}
              >
                {econ?.currency_unit && (
                  <Descriptions.Item label={t("cif.profile.currency")} span={2}>{econ.currency_unit}</Descriptions.Item>
                )}
                {inds.exchange_rate && (() => {
                  const i = inds.exchange_rate;
                  return <Descriptions.Item label={t("cif.profile.rateOfExchange", { year: i.year })} span={2}>{i.value != null ? i.value.toFixed(2) : "—"} {i.unit || ""}</Descriptions.Item>;
                })()}
                {inds.gdp_current_prices && (() => {
                  const i = inds.gdp_current_prices;
                  return <Descriptions.Item label={t("cif.profile.gdpCurrent", { year: i.year })} span={2}>{formatIndicatorValue(i.value, i.unit)} {i.unit || ""}</Descriptions.Item>;
                })()}
                {inds.gdp_per_capita && (() => {
                  const pc = inds.gdp_per_capita;
                  return <Descriptions.Item label={t("cif.profile.gdpPerCapita", { year: pc.year })} span={2}>{pc.value != null ? pc.value.toLocaleString("en-US") : "—"} {pc.unit || ""}</Descriptions.Item>;
                })()}
                {inds.education_expenditure && (() => {
                  const i = inds.education_expenditure;
                  return <Descriptions.Item label={t("cif.profile.eduExpenditure", { year: i.year })} span={2}>{i.value != null ? i.value.toFixed(2) : "—"} {i.unit || ""}</Descriptions.Item>;
                })()}
                {inds.health_expenditure && (() => {
                  const i = inds.health_expenditure;
                  return <Descriptions.Item label={t("cif.profile.healthExpenditure", { year: i.year })} span={2}>{i.value != null ? i.value.toFixed(2) : "—"} {i.unit || ""}</Descriptions.Item>;
                })()}
                {inds.unemployment_rate && (() => {
                  const i = inds.unemployment_rate;
                  return <Descriptions.Item label={t("cif.profile.unemploymentRate", { year: i.year })} span={2}>{i.value != null ? i.value.toFixed(2) : "—"} {i.unit || ""}</Descriptions.Item>;
                })()}
                {inds.fdi_inflows && (() => {
                  const i = inds.fdi_inflows;
                  return <Descriptions.Item label={t("cif.profile.fdiInflows", { year: i.year })} span={2}>{formatIndicatorValue(i.value, i.unit)} {i.unit || ""}</Descriptions.Item>;
                })()}
                {inds.oda_net && (() => {
                  const i = inds.oda_net;
                  return <Descriptions.Item label={t("cif.profile.odaNet", { year: i.year })} span={2}>{formatIndicatorValue(i.value, i.unit)} {i.unit || ""}</Descriptions.Item>;
                })()}
                {inds.merchandise_exports && (() => {
                  const i = inds.merchandise_exports;
                  return (
                    <Descriptions.Item label={t("cif.profile.merchExports", { year: i.year })} span={2}>
                      <div style={{ lineHeight: 1.6 }}>
                        <div>{formatIndicatorValue(i.value, i.unit)} {i.unit || ""}</div>
                        {econ?.export_partners && (
                          <div style={{ paddingLeft: 10, borderLeft: "2px solid #519BD6", fontStyle: "italic", fontSize: 13.5, marginTop: 4, lineHeight: 1.8 }}>
                            <div><strong>{t("cif.profile.mainExportPartners")}</strong> {econ.export_partners}</div>
                          </div>
                        )}
                      </div>
                    </Descriptions.Item>
                  );
                })()}
                {inds.merchandise_imports && (() => {
                  const i = inds.merchandise_imports;
                  return (
                    <Descriptions.Item label={t("cif.profile.merchImports", { year: i.year })} span={2}>
                      <div style={{ lineHeight: 1.6 }}>
                        <div>{formatIndicatorValue(i.value, i.unit)} {i.unit || ""}</div>
                        {econ?.import_partners && (
                          <div style={{ paddingLeft: 10, borderLeft: "2px solid #519BD6", fontStyle: "italic", fontSize: 13.5, marginTop: 4, lineHeight: 1.8 }}>
                            <div><strong>{t("cif.profile.mainImportPartners")}</strong> {econ.import_partners}</div>
                          </div>
                        )}
                      </div>
                    </Descriptions.Item>
                  );
                })()}
                {inds.intra_oic_exports && (() => {
                  const i = inds.intra_oic_exports;
                  return (
                    <Descriptions.Item label={t("cif.profile.intraOicExports", { year: i.year })} span={2}>
                      <div style={{ lineHeight: 1.6 }}>
                        <div>{formatIndicatorValue(i.value, i.unit)} {i.unit || ""}</div>
                        {econ?.oic_export_partners && (
                          <div style={{ paddingLeft: 10, borderLeft: "2px solid #519BD6", fontStyle: "italic", fontSize: 13.5, marginTop: 4, lineHeight: 1.8 }}>
                            <div><strong>{t("cif.profile.mainExportPartnersOic")}</strong> {econ.oic_export_partners}</div>
                          </div>
                        )}
                      </div>
                    </Descriptions.Item>
                  );
                })()}
                {inds.intra_oic_imports && (() => {
                  const i = inds.intra_oic_imports;
                  return (
                    <Descriptions.Item label={t("cif.profile.intraOicImports", { year: i.year })} span={2}>
                      <div style={{ lineHeight: 1.6 }}>
                        <div>{formatIndicatorValue(i.value, i.unit)} {i.unit || ""}</div>
                        {econ?.oic_import_partners && (
                          <div style={{ paddingLeft: 10, borderLeft: "2px solid #519BD6", fontStyle: "italic", fontSize: 13.5, marginTop: 4, lineHeight: 1.8 }}>
                            <div><strong>{t("cif.profile.mainImportPartnersOic")}</strong> {econ.oic_import_partners}</div>
                          </div>
                        )}
                      </div>
                    </Descriptions.Item>
                  );
                })()}
                {Di(t("cif.profile.naturalResources"), econ.natural_resources, 2)}
                {Di(t("cif.profile.mainAgriculturalProducts"), econ.main_agricultural_products, 2)}
                {extraItems(econ, KNOWN_ECO)}
              </Descriptions>
            </Col>
            {econ.economy_overview && (
            <Col xs={24} md={8}>
              <Card size="small"
                style={{
                  background: "#d4e6f5",
                  borderRadius: 8,
                  border: "1px solid #e8e8e8",
                  height: "100%",
                }}
              >
                <Text strong style={{ fontSize: 13, color: "#2F83C5", display: "block", marginBottom: 4 }}>{t("cif.profile.economicOverview")}</Text>
                <Paragraph style={{ fontSize: 13, margin: 0, lineHeight: 1.6, color: "#444" }}>
                  {econ.economy_overview}
                </Paragraph>
              </Card>
            </Col>
            )}
          </Row>
        </Panel>
        )}

        <Panel
          header={sectionHeader(sectionIcons.figures || <NumberOutlined />, `5. ${t("cif.profile.inFigures", { country: (country?.name || "COUNTRY").toUpperCase() })}`)}
          key="5"
          style={{ borderRadius: 8, marginBottom: 2 }}
        >
          {allIndicators?.categories && allIndicators.categories.length > 0 && (
            <>
              <Input.Search
                placeholder={t("cif.profile.searchIndicators")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                style={{ maxWidth: 400, marginBottom: 16 }}
              />
              <Collapse
                ghost
                expandIconPosition="end"
                style={{ borderRadius: 8 }}
                activeKey={activeCategories}
                onChange={(keys) => {
                  if (!debouncedSearch.trim()) setOpenCats(keys);
                }}
              >
                {allIndicators.categories
                  .map((cat) => {
                    if (!debouncedSearch.trim()) {
                      return cat.indicators.length > 0 ? cat : null;
                    }
                    const q = debouncedSearch.toLowerCase().trim();
                    const matchingInds = cat.indicators.filter((ind) =>
                      ind.indicator_name.toLowerCase().includes(q)
                    );
                    if (cat.cat_name.toLowerCase().includes(q)) {
                      return cat.indicators.length > 0 ? cat : null;
                    }
                    return matchingInds.length > 0
                      ? { ...cat, indicators: matchingInds }
                      : null;
                  })
                  .filter(Boolean)
                  .sort((a, b) => a.cat_name.localeCompare(b.cat_name))
                  .map((cat) => (
                    <Panel
                      key={String(cat.cat_code)}
                      header={
                        <Text strong style={{ fontSize: 13 }}>{cat.cat_name}</Text>
                      }
                      style={{
                        borderRadius: 6,
                        marginBottom: 2,
                        border: "1px solid #e8e8e8",
                        background: "#fff",
                      }}
                    >
                      <Table
                        dataSource={[...cat.indicators].sort((a, b) => a.indicator_name.localeCompare(b.indicator_name))}
                        columns={[
                          { title: t("cif.profile.indicator"), dataIndex: "indicator_name", key: "indicator_name", render: (v) => <Text style={{ fontSize: 13 }}>{v}</Text> },
                          { title: t("cif.profile.year"), dataIndex: "year", key: "year", width: 65, render: (y) => <Tag style={{ fontSize: 11, border: "none", background: "#d4e6f5" }}>{y}</Tag> },
                          { title: t("cif.profile.value"), dataIndex: "value", key: "value", width: 140, render: (v) => <Text strong style={{ fontSize: 13 }}>{v || "—"}</Text> },
                          { title: t("cif.profile.unit"), dataIndex: "unit", key: "unit", render: (u) => u ? <Text style={{ fontSize: 12, color: "#000" }}>{u}</Text> : "—" },
                          {
                            title: "", key: "action", width: 95,
                            render: (_, record) => (
                              <Button
                                type="text"
                                size="small"
                                icon={<LineChartOutlined />}
                                onClick={() => openTimeSeries(record)}
                                title={t("cif.profile.viewTimeSeries")}
                              >
                                {t("cif.profile.timeplot")}
                              </Button>
                            ),
                          },
                        ]}
                        pagination={{ pageSize: 15, size: "small", hideOnSinglePage: true }}
                        size="small"
                        rowKey="indicator_id"
                        style={{ borderRadius: 6, overflow: "hidden" }}
                      />
                    </Panel>
                  ))}
              </Collapse>
            </>
          )}
        </Panel>

        {logi && (
        <Panel
          header={sectionHeader(sectionIcons.logistics, `6. ${t("cif.sections.6.title").toUpperCase()}`)}
          key="6"
          style={{ borderRadius: 8, marginBottom: 2 }}
        >
          <Row gutter={24}>
            <Col xs={24} md={16}>
              <Descriptions column={1} size="small" bordered
                style={{ borderRadius: 8, overflow: "hidden" }}
                styles={{
                  label: { background: "#d4e6f5", fontWeight: 600, fontSize: 13, color: "#000" },
                  content: { fontSize: 14, color: "#000" },
                }}
              >
                {Di(t("cif.profile.timeZone"), logi.time_difference)}
                {Di(t("cif.profile.countryCode"), logi.country_code)}
                {Di(t("cif.profile.electricity"), logi.electricity)}
                {logi.visa_issues && <Descriptions.Item label={t("cif.profile.visaInformation")} span={2}>{logi.visa_issues}</Descriptions.Item>}
                {logi.places_to_visit && <Descriptions.Item label={t("cif.profile.placesToVisit")} span={2}>{logi.places_to_visit}</Descriptions.Item>}
                {extraItems(logi, KNOWN_LOG)}
              </Descriptions>
            </Col>
            {(logi.police_number || logi.ambulance_number || logi.fire_number) && (
            <Col xs={24} md={8}>
              <Card size="small" style={{ background: "#d4e6f5", borderRadius: 8, border: "1px solid #e8e8e8" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div>
                    <Space>
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: "#2F83C5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#fff" }}>
                        <PhoneOutlined />
                      </div>
                      <Text strong style={{ fontSize: 13 }}>{t("cif.profile.emergencyNumbers")}</Text>
                    </Space>
                  </div>
                  <Row gutter={8}>
                    {logi.police_number && (
                    <Col span={8}>
                      <Card size="small" style={{ textAlign: "center", background: "#2F83C5", color: "#fff", border: "none", borderRadius: 8, boxShadow: "0 2px 8px rgba(47, 131, 197,0.3)" }}>
                        <Text strong style={{ color: "#fff", fontSize: 12 }}>{t("cif.profile.police")}</Text>
                        <br />
                        <Text style={{ color: "#fff", fontSize: 17, fontWeight: 700 }}>{logi.police_number}</Text>
                      </Card>
                    </Col>
                    )}
                    {logi.ambulance_number && (
                    <Col span={8}>
                      <Card size="small" style={{ textAlign: "center", background: "#2F83C5", color: "#fff", border: "none", borderRadius: 8, boxShadow: "0 2px 8px rgba(47, 131, 197,0.3)" }}>
                        <Text strong style={{ color: "#fff", fontSize: 12 }}>{t("cif.profile.ambulance")}</Text>
                        <br />
                        <Text style={{ color: "#fff", fontSize: 17, fontWeight: 700 }}>{logi.ambulance_number}</Text>
                      </Card>
                    </Col>
                    )}
                    {logi.fire_number && (
                    <Col span={8}>
                      <Card size="small" style={{ textAlign: "center", background: "#2F83C5", color: "#fff", border: "none", borderRadius: 8, boxShadow: "0 2px 8px rgba(47, 131, 197,0.3)" }}>
                        <Text strong style={{ color: "#fff", fontSize: 12 }}>{t("cif.profile.fire")}</Text>
                        <br />
                        <Text style={{ color: "#fff", fontSize: 17, fontWeight: 700 }}>{logi.fire_number}</Text>
                      </Card>
                    </Col>
                    )}
                  </Row>
                </Space>
              </Card>
            </Col>
            )}
          </Row>
        </Panel>
        )}

        {linkEntries.length > 0 && (
        <Panel
          header={sectionHeader(sectionIcons.links, `7. ${t("cif.sections.7.title").toUpperCase()}`)}
          key="7"
          style={{ borderRadius: 8, marginBottom: 2 }}
        >
          <Row gutter={[12, 12]}>
            {linkEntries.map((link, idx) => (
              <Col xs={24} sm={12} md={8} key={idx}>
                <Card
                  size="small"
                  hoverable
                  onClick={() => window.open(link.url, "_blank")}
                  style={{
                    borderRadius: 8,
                    border: "1px solid #e8e8e8",
                    transition: "all 0.2s",
                    cursor: "pointer",
                  }}
                  styles={{ body: { padding: "10px 14px" } }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2F83C5"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(47, 131, 197,0.12)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e8e8e8"; e.currentTarget.style.boxShadow = ""; }}
                >
                  <Space>
                    <div style={{
                      width: 28, height: 28, borderRadius: 6,
                      background: "#2F83C5",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: 13,
                    }}>
                      <BankOutlined />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <Text strong style={{ fontSize: 13, display: "block" }}>{link.name}</Text>
                      <Text style={{ fontSize: 12, wordBreak: "break-all", lineHeight: 1.3, color: "#000" }}>{link.url}</Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Panel>
        )}
      </Collapse>

      <div style={{ textAlign: "right", marginTop: 24, marginBottom: 8 }}>
        <ExportButton onExport={handleExportReport}>
          {t("cif.profile.exportReport")}
        </ExportButton>
      </div>

      <TimeSeriesModal
        visible={tsModal.visible}
        onClose={() => setTsModal({ visible: false, countryName: "", indicatorName: "", unit: "", data: null })}
        countryName={tsModal.countryName}
        indicatorName={tsModal.indicatorName}
        unit={tsModal.unit}
        data={tsModal.data}
      />

    </div>
  );
}
